import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import {
  Button,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
  PageShell,
  Skeleton,
  toast,
} from '@/components/ui';
import { Plus } from 'lucide-react';
import {
  useAdminCategoriesQuery,
  useAdminCategoryMutation,
  type AdminCategoryView,
  type CategoryCreatePayload,
  type CategoryUpdatePayload,
} from '../hooks/useAdminCategoriesQuery';
import { CategoryTree } from '../components/CategoryTree';
import { CategoryFormModal } from '../components/CategoryFormModal';
import { CategoryProductsPanel } from '../components/CategoryProductsPanel';

interface FormModalState {
  open: boolean;
  mode: 'create' | 'edit';
  parentCategory: AdminCategoryView | null;
  editTarget: AdminCategoryView | null;
}

const INITIAL_FORM: FormModalState = {
  open: false,
  mode: 'create',
  parentCategory: null,
  editTarget: null,
};

export const CategoryListPage = () => {
  const { t } = useTranslation('common');
  const query = useAdminCategoriesQuery();
  const { create, update, remove } = useAdminCategoryMutation();

  const [formModal, setFormModal] = useState<FormModalState>(INITIAL_FORM);
  const [deleteTarget, setDeleteTarget] = useState<AdminCategoryView | null>(null);
  // 삭제가 거부됐을 때의 사유. 다이얼로그 안에 보여준다 — 토스트는 금방 사라져 "연결 상품 N개" 같은
  // 조치에 필요한 정보를 놓치기 쉽다.
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // ID43: [연결 상품 관리] 대상 카테고리. 트리에서 선택하면 하단 패널 노출.
  const [productsTarget, setProductsTarget] = useState<AdminCategoryView | null>(null);

  const openCreateRoot = () =>
    setFormModal({ open: true, mode: 'create', parentCategory: null, editTarget: null });

  const openAddChild = (parent: AdminCategoryView) =>
    setFormModal({ open: true, mode: 'create', parentCategory: parent, editTarget: null });

  const openEdit = (category: AdminCategoryView) =>
    setFormModal({ open: true, mode: 'edit', parentCategory: null, editTarget: category });

  const closeForm = () => setFormModal(INITIAL_FORM);

  const handleFormSubmit = (payload: CategoryCreatePayload | CategoryUpdatePayload) => {
    if (formModal.mode === 'create') {
      create.mutate(payload as CategoryCreatePayload, {
        onSuccess: closeForm,
        onError: (e) => toast.error(friendlyMessage(e, t('cms.categories.createError', '카테고리 등록에 실패했습니다.'))),
      });
    } else if (formModal.editTarget) {
      update.mutate(
        { categoryId: formModal.editTarget.categoryId, payload: payload as CategoryUpdatePayload },
        {
          onSuccess: closeForm,
          onError: (e) => toast.error(friendlyMessage(e, t('cms.categories.updateError', '카테고리 수정에 실패했습니다.'))),
        },
      );
    }
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    setDeleteError(null);
    remove.mutate(deleteTarget.categoryId, {
      onSuccess: () => setDeleteTarget(null),
      onError: (e) => setDeleteError(friendlyMessage(e, t('cms.categories.deleteError', '카테고리 삭제에 실패했습니다.'))),
    });
  };

  const closeDeleteDialog = () => {
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const items = query.data ?? [];
  const formPending = create.isPending || update.isPending;

  return (
    <PageShell
      title={t('cms.categories.title')}
      actions={
        <Button size="sm" onClick={openCreateRoot}>
          <Plus size={14} aria-hidden="true" />
          {t('cms.categories.createRoot')}
        </Button>
      }
    >
      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[var(--density-default-row-h)] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('cms.categories.loadFailed')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('cms.retry')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <CategoryTree
            roots={items}
            selectedCategoryId={productsTarget?.categoryId ?? null}
            onAddChild={openAddChild}
            onEdit={openEdit}
            onDelete={setDeleteTarget}
            onManageProducts={setProductsTarget}
          />
          {productsTarget ? <CategoryProductsPanel category={productsTarget} /> : null}
        </div>
      )}

      <CategoryFormModal
        open={formModal.open}
        onOpenChange={(open) => { if (!open) closeForm(); }}
        mode={formModal.mode}
        parentCategory={formModal.parentCategory}
        editTarget={formModal.editTarget}
        isPending={formPending}
        onSubmit={handleFormSubmit}
      />

      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) closeDeleteDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('cms.categories.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('cms.categories.deleteDesc', { name: deleteTarget?.name ?? '' })}
            </DialogDescription>
          </DialogHeader>
          {deleteError ? (
            <p
              role="alert"
              className="rounded-md p-3 text-sm"
              style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
            >
              {deleteError}
            </p>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost" size="sm">{t('cms.cancel')}</Button>
            </DialogClose>
            <Button
              type="button"
              size="sm"
              variant="danger"
              disabled={remove.isPending}
              onClick={handleDeleteConfirm}
            >
              {t('cms.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
